import React, { useState, useRef } from 'react';
import { Button, Badge, Popover, Modal } from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import './NotificationBell.css';

type NoticeMeta = {
  source?: string;
  offset_minutes?: number;
  start_at?: string;
  end_at?: string;
  event_title?: string;
  client_label?: string;
  notes?: string;
};

type Notice = {
  id: number;
  title?: string;
  message?: string;
  read?: boolean;
  notification_type?: string;
  priority?: string;
  created_at?: string;
  action_url?: string | null;
  meta_data?: NoticeMeta | null;
};

export interface NotificationBellProps {
  /** 'light' = dark icon on light bg (MainLayout, Partnership), 'dark' = white icon on dark nav */
  variant?: 'light' | 'dark';
  notifications: Notice[];
  loading?: boolean;
  onMarkAsRead?: (id: number) => void | Promise<void>;
  onMarkAllAsRead?: () => void | Promise<void>;
  onClear?: (scope: 'new' | 'all') => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  onNotificationClick?: (notification: any) => void;
  /** Optional custom render for notification details in the modal */
  renderNotificationDetails?: (notification: any) => React.ReactNode;
  /** Hide the bell when no notification backend (e.g. partnership excluded from main API) */
  hideWhenEmpty?: boolean;
}

const OFFSETS: Record<number, { short: string; long: string; tone: string }> = {
  60: { short: '1h', long: 'Daqui a 1 hora', tone: 'soon' },
  30: { short: '30m', long: 'Daqui a 30 minutos', tone: 'near' },
  5: { short: '5m', long: 'Daqui a 5 minutos', tone: 'now' },
};

function clockParts(raw?: string | null) {
  if (!raw) return { time: '', dateShort: '', dateLong: '' };
  const parsed = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return { time: '', dateShort: '', dateLong: '' };
  return {
    time: parsed.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
    dateShort: parsed.toLocaleDateString('pt-PT'),
    dateLong: parsed.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' }),
  };
}

function noticeIcon(n: Notice) {
  if (n.meta_data?.source === 'backoffice_exclusividade') return <FileSearchOutlined />;
  if (n.meta_data?.source === 'ccc_calendar' || n.meta_data?.source === 'grh_interview_cluster') {
    return <CalendarOutlined />;
  }
  switch (n.notification_type) {
    case 'activity':
    case 'event_creation':
    case 'reminder':
      return <CalendarOutlined />;
    case 'warning':
    case 'error':
    case 'duplicate_lead':
      return <ExclamationCircleOutlined />;
    case 'success':
      return <CheckCircleOutlined />;
    case 'info':
      return <InfoCircleOutlined />;
    default:
      return <BellOutlined />;
  }
}

function noticeView(n: Notice) {
  const offset = OFFSETS[Number(n.meta_data?.offset_minutes)];
  const message = n.message || '';
  const splitAt = message.indexOf(' começa às ');
  const eventName = n.meta_data?.event_title || (splitAt > 0 ? message.slice(0, splitAt) : n.title || 'Notificação');
  const when = message.match(/começa às (\d{2}:\d{2}) de (\d{2}\/\d{2}\/\d{4})/);
  const start = clockParts(n.meta_data?.start_at);
  const end = clockParts(n.meta_data?.end_at);
  const time = start.time || when?.[1] || '';
  const dateShort = start.dateShort || when?.[2] || '';
  const dateLong = start.dateLong || when?.[2] || '';
  const isCalendar =
    n.meta_data?.source === 'ccc_calendar' ||
    n.meta_data?.source === 'grh_interview_cluster' ||
    (splitAt > 0 && Boolean(time));
  return {
    eventName,
    time,
    endTime: end.time,
    date: dateShort,
    dateLong,
    notes: (n.meta_data?.notes || '').trim(),
    client: n.meta_data?.client_label || '',
    long: offset?.long || n.title || 'Notificação',
    tone: offset?.tone || (n.priority === 'urgent' ? 'now' : n.priority === 'high' ? 'near' : 'soon'),
    isCalendar,
  };
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  variant = 'light',
  notifications = [],
  loading = false,
  onMarkAsRead,
  onMarkAllAsRead,
  onClear,
  onRefresh,
  onNotificationClick,
  renderNotificationDetails,
  hideWhenEmpty = false,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tab, setTab] = useState<'new' | 'all'>('new');
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const unread = notifications.filter((n) => !n.read);
  const unreadCount = unread.length;
  const visible = tab === 'new' ? unread : notifications;
  const selectedView = selectedNotification ? noticeView(selectedNotification) : null;
  const iconColor = variant === 'dark' ? 'white' : '#222';
  const iconSize = variant === 'dark' ? 20 : 26;

  const handleNotificationClick = (n: Notice) => {
    setSelectedNotification(n);
    setModalVisible(true);
    setPopoverOpen(false);
    if (!n.read && onMarkAsRead) {
      onMarkAsRead(n.id);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedNotification(null);
  };

  const openLinkedPage = () => {
    if (!selectedNotification) return;
    onNotificationClick?.(selectedNotification);
    closeModal();
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (open && onRefresh) {
      onRefresh();
    }
  };

  const overlay = (
    <div className="notification-dropdown">
      <div className="notification-dropdown-header">
        <span className="notification-dropdown-title">
          Notificações
          {unreadCount > 0 && <span className="notification-dropdown-count">{unreadCount}</span>}
        </span>
        <div className="notification-dropdown-actions">
          {onMarkAllAsRead && (
            <Button
              type="text"
              icon={<CheckOutlined />}
              onClick={onMarkAllAsRead}
              title="Marcar todas como lidas"
            />
          )}
          {onRefresh && (
            <Button
              type="text"
              icon={<ReloadOutlined spin={loading} />}
              onClick={onRefresh}
              title="Atualizar"
            />
          )}
        </div>
      </div>
      <div className="notification-dropdown-tabs">
        <button
          type="button"
          className={`notification-dropdown-tab ${tab === 'new' ? 'active' : ''}`}
          onClick={() => setTab('new')}
        >
          Novas
          {unreadCount > 0 && <span>{unreadCount}</span>}
        </button>
        <button
          type="button"
          className={`notification-dropdown-tab ${tab === 'all' ? 'active' : ''}`}
          onClick={() => setTab('all')}
        >
          Todas
          {notifications.length > 0 && <span>{notifications.length}</span>}
        </button>
        {onClear && visible.length > 0 && (
          <button
            type="button"
            className="notification-dropdown-clear"
            onClick={() => onClear(tab)}
            title={tab === 'new' ? 'Limpar novas' : 'Limpar todas'}
          >
            <DeleteOutlined />
            Limpar
          </button>
        )}
      </div>
      <div className="notification-dropdown-body">
        {loading ? (
          <div className="notification-dropdown-loading">
            A carregar notificações...
          </div>
        ) : visible.length === 0 ? (
          <div className="notification-dropdown-empty">
            <div className="notification-dropdown-empty-icon">
              <BellOutlined />
            </div>
            <div className="notification-dropdown-empty-title">
              {tab === 'new' ? 'Sem notificações novas' : 'Sem notificações'}
            </div>
            <div className="notification-dropdown-empty-desc">
              {tab === 'new'
                ? 'As já lidas ficam no separador Todas'
                : 'Será notificado quando houver novidades'}
            </div>
          </div>
        ) : (
          visible.map((n, idx) => {
            const view = noticeView(n);
            return (
              <button
                key={n.id || idx}
                type="button"
                onClick={() => handleNotificationClick(n)}
                className={`notification-dropdown-item ${!n.read ? 'unread' : ''} tone-${view.tone}`}
              >
                <span className="notification-badge">{noticeIcon(n)}</span>
                <span className="notification-dropdown-copy">
                  <span className="notification-dropdown-item-text">{view.eventName}</span>
                  <span className="notification-dropdown-item-meta">
                    {view.isCalendar && view.time
                      ? `${view.long} · ${view.time}${view.date ? ` · ${view.date}` : ''}`
                      : n.message || view.long}
                  </span>
                </span>
                {!n.read && <span className="notification-dropdown-dot" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const buttonStyle =
    variant === 'dark'
      ? { width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', border: 'none' }
      : {
          width: 48,
          height: 48,
          borderRadius: 12,
          background: 'none',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };

  if (hideWhenEmpty && notifications.length === 0 && !loading) {
    return null;
  }

  return (
    <>
      <Popover
        content={overlay}
        trigger="click"
        placement="bottomRight"
        open={popoverOpen}
        onOpenChange={handlePopoverOpenChange}
        overlayClassName="notification-bell-popover"
      >
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <Button
            ref={btnRef}
            type="text"
            icon={<BellOutlined style={{ fontSize: iconSize, color: iconColor }} />}
            style={buttonStyle}
            title="Notificações"
          />
        </Badge>
      </Popover>

      <Modal
        title={
          <div className="notification-modal-heading">
            {selectedView?.isCalendar ? (
              <CalendarOutlined />
            ) : selectedNotification?.meta_data?.source === 'backoffice_exclusividade' ? (
              <FileSearchOutlined />
            ) : (
              <BellOutlined />
            )}
            <span>
              {selectedView?.isCalendar
                ? 'Lembrete do calendário'
                : selectedNotification?.meta_data?.source === 'backoffice_exclusividade'
                  ? 'Exclusividade'
                  : 'Notificação'}
            </span>
          </div>
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={
          <div className="notification-modal-actions">
            <Button onClick={closeModal}>Fechar</Button>
            {selectedNotification?.action_url && (
              <Button
                type="primary"
                icon={
                  String(selectedNotification.action_url).includes('proposal-board')
                    ? <FileSearchOutlined />
                    : <CalendarOutlined />
                }
                onClick={openLinkedPage}
              >
                {String(selectedNotification.action_url).includes('proposal-board')
                  ? 'Abrir quadro'
                  : 'Abrir calendário'}
              </Button>
            )}
          </div>
        }
        width={480}
        className="notification-bell-modal"
        destroyOnClose
      >
        {selectedNotification && selectedView && (
          <div className="notification-detail">
            <div className="notification-detail-kicker">
              <span className="notification-badge">{noticeIcon(selectedNotification)}</span>
              <span>{selectedView.long}</span>
            </div>
            <h3 className="notification-detail-title">{selectedView.eventName}</h3>
            {selectedView.time && (
              <div className="notification-detail-when">
                <span className="notification-detail-clock">{selectedView.time}</span>
                {selectedView.endTime && (
                  <span className="notification-detail-end">– {selectedView.endTime}</span>
                )}
                {selectedView.dateLong && (
                  <span className="notification-detail-date">{selectedView.dateLong}</span>
                )}
              </div>
            )}
            {selectedView.client && (
              <p className="notification-detail-client">Cliente: {selectedView.client}</p>
            )}
            <div className="notification-detail-notes">
              <span className="notification-detail-notes-label">Descrição</span>
              <p>
                {selectedView.isCalendar
                  ? selectedView.notes || 'Sem descrição.'
                  : selectedNotification.message || 'Sem descrição.'}
              </p>
            </div>
            {renderNotificationDetails && (
              <div className="notification-details">
                {renderNotificationDetails(selectedNotification)}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default NotificationBell;
